import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../LoadingSpinner';
import {
  Mail,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  RefreshCw,
  Search,
  ChevronRight,
  AlertTriangle,
  Send
} from 'lucide-react';

interface BroadcastCampaign {
  id: string;
  subject: string;
  status: string;
  total_contacts: number;
  sent_count: number;
  failed_count: number;
  created_at: string;
  started_sending_at: string | null;
  completed_at: string | null;
}

interface BroadcastHistoryListProps {
  onSelectBroadcast: (id: string) => void;
  refreshTrigger?: number;
}

export function BroadcastHistoryList({ onSelectBroadcast, refreshTrigger }: BroadcastHistoryListProps) {
  const [loading, setLoading] = useState(true);
  const [broadcasts, setBroadcasts] = useState<BroadcastCampaign[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadBroadcasts();
  }, [refreshTrigger]);

  const loadBroadcasts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('email_campaigns')
        .select('id, subject, status, total_contacts, sent_count, failed_count, created_at, started_sending_at, completed_at')
        .eq('campaign_type', 'admin_broadcast')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBroadcasts(data || []);
    } catch (err) {
      console.error('Error loading broadcasts:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
      draft: {
        label: 'Concept',
        className: 'bg-gray-100 text-gray-700',
        icon: <Clock className="w-3.5 h-3.5" />,
      },
      sending: {
        label: 'Bezig...',
        className: 'bg-amber-100 text-amber-700',
        icon: <Send className="w-3.5 h-3.5 animate-pulse" />,
      },
      completed: {
        label: 'Verzonden',
        className: 'bg-green-100 text-green-700',
        icon: <CheckCircle className="w-3.5 h-3.5" />,
      },
      failed: {
        label: 'Mislukt',
        className: 'bg-red-100 text-red-700',
        icon: <XCircle className="w-3.5 h-3.5" />,
      },
    };
    return configs[status] || configs.draft;
  };

  const filtered = broadcasts.filter((b) =>
    b.subject?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (broadcasts.length === 0) {
    return (
      <div className="text-center py-16">
        <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-1">Nog geen broadcasts verzonden</h3>
        <p className="text-sm text-gray-500">
          Verzonden broadcasts verschijnen hier automatisch.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Zoek op onderwerp..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={loadBroadcasts}
          className="p-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          title="Vernieuw lijst"
        >
          <RefreshCw className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="divide-y divide-gray-100">
          {filtered.map((broadcast) => {
            const statusConfig = getStatusConfig(broadcast.status);
            const successRate = broadcast.total_contacts > 0
              ? Math.round((broadcast.sent_count / broadcast.total_contacts) * 100)
              : 0;
            const hasFails = broadcast.failed_count > 0;

            return (
              <button
                key={broadcast.id}
                onClick={() => onSelectBroadcast(broadcast.id)}
                className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2.5 mb-1.5">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {broadcast.subject || '(Geen onderwerp)'}
                      </h3>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${statusConfig.className}`}>
                        {statusConfig.icon}
                        {statusConfig.label}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDate(broadcast.completed_at || broadcast.started_sending_at || broadcast.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {broadcast.total_contacts} ontvangers
                      </span>
                      {broadcast.status === 'completed' && (
                        <>
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-3.5 h-3.5" />
                            {broadcast.sent_count} verzonden
                          </span>
                          {hasFails && (
                            <span className="flex items-center gap-1 text-red-600">
                              <AlertTriangle className="w-3.5 h-3.5" />
                              {broadcast.failed_count} mislukt
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0">
                    {broadcast.status === 'completed' && (
                      <div className="hidden sm:flex items-center gap-1.5">
                        <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${hasFails ? 'bg-amber-500' : 'bg-green-500'}`}
                            style={{ width: `${successRate}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-500">{successRate}%</span>
                      </div>
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-gray-400 text-center">
        {filtered.length} van {broadcasts.length} broadcast{broadcasts.length !== 1 ? 's' : ''}
      </p>
    </div>
  );
}
